import React from 'react';
import { Neo, NeoGrid, Views } from '@singularsystems/neo-react';
import RawGenericMappingVM from './RawGenericMappingVM';
import { observer } from 'mobx-react';
import RawGenericMappingLookup from '../Models/Lookups/RawGenericMappingLookup';
import { TextConstants } from '../../../Common/TextConstants';
import ManageRawGenericMappingComponentView from '../Components/ManageRawGenericMappingComponentView';
import Loading from '../../../App/Components/Loading/Loading';
import { GenericMappings } from '../../../AccessControl/Models/Commands/Security/Roles';

@observer
export default class RawGenericMappingView extends Views.ViewBase<RawGenericMappingVM> {

    constructor(props: unknown) {
        super("Raw Generic Mapping", RawGenericMappingVM, props);
    }

    public render() {
        return (
            <div>
                {(this.viewModel.taskRunner.isBusy) && <Loading />}
                {this.viewModel.showManageMappingsComponent &&
                    <ManageRawGenericMappingComponentView
                        viewModel={this.viewModel.manageRawGenericMappingVM}
                        onSave={() => this.viewModel.saveGenericMapping()}
                        onCancel={() => this.viewModel.cancel()} />
                }
                {!this.viewModel.showManageMappingsComponent &&
                    <Neo.Card title={TextConstants.Titles.GenericMappings} className="m-1">
                        {this.viewModel.authorisationService.hasRole(GenericMappings.AddNew) &&
                            <div className="row pb-3">
                                <div className="col text-right">
                                    <Neo.Button
                                        size="sm" className="btn btn-default"
                                        variant="primary"
                                        onClick={() => this.viewModel.addNewGenericMapping()}>
                                        {TextConstants.Buttons.AddGenericMapping}
                                    </Neo.Button>
                                </div>
                            </div>
                        }
                        <>
                            <Neo.Form
                                model={this.viewModel.rawGenericMappingSearchCriteria}
                                onSubmit={() => this.viewModel.pageManager.refreshData()}>
                                <div className="row">
                                    <div className="col-lg-3">
                                        <Neo.FormGroup
                                            bind={this.viewModel.rawGenericMappingSearchCriteria.meta.sourceTable}
                                            label={TextConstants.Labels.SourceTable} />
                                    </div>
                                    <div className="col-lg-3">
                                        <Neo.FormGroup
                                            bind={this.viewModel.rawGenericMappingSearchCriteria.meta.sourceField}
                                            label={TextConstants.Labels.SourceField} />
                                    </div>
                                    <div className="col-lg-3">
                                        <Neo.FormGroup
                                            bind={this.viewModel.rawGenericMappingSearchCriteria.meta.destinationTableId}
                                            label={TextConstants.Labels.DestinationTable}
                                            select={{ items: this.viewModel.destinationTableList }}>
                                        </Neo.FormGroup>
                                    </div>
                                    <div className="col-lg-3">
                                        <Neo.FormGroup
                                            bindContext={this.viewModel.rawGenericMappingSearchCriteria.meta.destinationFieldId}
                                            label={TextConstants.Labels.DestinationField}>
                                            <Neo.AutoCompleteDropDown
                                                itemSource={(searchValue) => this.viewModel.rawGenericMappingApiClient.getDestinationFieldListByCriteria(searchValue, this.viewModel.rawGenericMappingSearchCriteria.meta.destinationTableId.value as number)}
                                                bind={this.viewModel.rawGenericMappingSearchCriteria.meta.destinationFieldId}
                                                displayMember={"destinationFieldName"}
                                                items={this.viewModel.rawGenericMappingApiClient.getDestinationFieldListByCriteria(" ", this.viewModel.rawGenericMappingSearchCriteria.meta.destinationTableId.value as number).then((arr) => arr.data.slice(0, 10)
                                                    .sort((x, y) => x.destinationFieldName.localeCompare(y.destinationFieldName)))}
                                            />
                                        </Neo.FormGroup>
                                    </div>
                                </div>
                                <div className="row">
                                    <div className="col-lg-3">
                                        <Neo.FormGroup
                                            bind={this.viewModel.rawGenericMappingSearchCriteria.meta.defaultValue}
                                            label={TextConstants.Labels.DefaultValue} />
                                    </div>
                                    <div className="col-lg-1">
                                        <Neo.FormGroup
                                            bind={this.viewModel.rawGenericMappingSearchCriteria.meta.iteration}
                                            label={TextConstants.Labels.Iteration} />
                                    </div>
                                    <div className="col-lg-2 pt-4">
                                        <Neo.FormGroup 
                                            bind={this.viewModel.rawGenericMappingSearchCriteria.meta.isGenericJoinField} 
                                            label={TextConstants.Labels.GenericJoinField} />
                                    </div>
                                </div>
                            </Neo.Form>
                        </>
                        <div>
                            <Neo.Pager pageManager={this.viewModel.pageManager} pageControlProps={{ pageSizeLabel: "Show " }}>
                                <div className="horizontalScroll inner-container">
                                    {this.viewModel.pageManager.totalRecords > 0 &&
                                        <NeoGrid.Grid<RawGenericMappingLookup> className={"table-striped"}>
                                            {(item, meta) => (
                                                <NeoGrid.RowGroup>
                                                    <NeoGrid.Row key={item.rawGenericMappingID} className="twoRowsPerGridItem">
                                                        <NeoGrid.Column display={meta.fileSpecProperties} label={TextConstants.Labels.FileType} wrap="true" />
                                                        <NeoGrid.Column display={meta.rawGenericSourceTableName} label={TextConstants.Labels.SourceTable} />
                                                        <NeoGrid.Column display={meta.rawGenericDestinationTableName} label={TextConstants.Labels.DestinationTable} colSpan={3} />
                                                        <NeoGrid.Column display={meta.defaultValue} />
                                                        <NeoGrid.Column display={meta.genericJoinFieldInd} label={TextConstants.Labels.GenericJoinField} />
                                                        <NeoGrid.Column display={meta.iteration} />
                                                    </NeoGrid.Row>
                                                    <NeoGrid.Row className="twoRowsPerGridItem">
                                                        <NeoGrid.Column display={meta.rawGenericSourceField} label={TextConstants.Labels.SourceField} />
                                                        <NeoGrid.Column display={meta.rawGenericDestinationField} label={TextConstants.Labels.DestinationField} />
                                                        <NeoGrid.Column display={meta.calculation} label={TextConstants.Labels.CalculationLogic} colSpan={5} />
                                                        <NeoGrid.ButtonColumn label={TextConstants.Labels.Edit} className="text-center">
                                                            {this.viewModel.authorisationService.hasRole(GenericMappings.Edit) &&
                                                                <Neo.Button
                                                                    onClick={() => this.viewModel.editGenericMapping(item)}
                                                                    variant="primary" isOutline className="btn-small">
                                                                    <i className="fa fa-edit" />
                                                                </Neo.Button>
                                                            }
                                                        </NeoGrid.ButtonColumn>
                                                    </NeoGrid.Row>
                                                </NeoGrid.RowGroup>
                                            )}
                                        </NeoGrid.Grid>
                                    }
                                </div>
                            </Neo.Pager>
                        </div>
                    </Neo.Card>
                }

            </div>
        );
    }
}